import { Component, EventEmitter, HostBinding, Input, Output } from '@angular/core'
import { formatDisplayHoursToMinutes, formatHours } from '@app/shared/utils/utils'

import { Role } from '@app/features/scoping/models/role.model'
import { ScopeComponent } from '@app/features/scoping/models/component.model'
import { ScopeElementsService } from '../services/scope-elements.service'
import { UpdateRolePayload, ScopeElementsConfig } from '../models'
import { plainToInstance } from 'class-transformer'
import { TableColumnKey } from '@app/shared/components/ui-components/scope-ui-table/table-column-key.enum'
import { Privilege } from '@app/core/model/enums/privilege.enum'
import { getCrFieldToDisplay } from '../utils/change-request/get-cr-field-to-display.util'
import { RoleWithChanges } from '../models/entities-with-changes/role-with-changes.interface'
import { showCrDifferences, getNumberDeltaValue, getRoleOldSellingRate } from '../utils/change-request'
import { FormatHoursPipe } from '@app/shared/pipe/formatHours.pipe'
import { ConvertCurrencyPipe } from '@app/shared/pipe/convertCurrency.pipe'
import { isNil } from 'lodash'
import { DEFAULT_PLACEHOLDER } from '@app/shared/constants/default-placeholder.const'
import { Money } from '@app/features/scope-overview/model/money.model'

@Component({
  selector: 'scope-roles-row',
  templateUrl: './roles-row.component.html',
  styleUrls: ['./roles-row.component.scss'],
  providers: [FormatHoursPipe],
})
export class RolesRowComponent {
  @Input() level: number
  @Input() role: RoleWithChanges
  @Input() config: ScopeElementsConfig
  @Input() component: ScopeComponent

  @Output() onOverrideRateCardRoleHours = new EventEmitter<UpdateRolePayload>()
  @Output() onUpdateRole = new EventEmitter<UpdateRolePayload>()

  constructor(
    protected scopeElementsService: ScopeElementsService,
    private formatHoursPipe: FormatHoursPipe,
    private convertCurrencyPipe: ConvertCurrencyPipe
  ) {}

  overrideRateCardRoleHours() {
    return () => this.onOverrideRateCardRoleHours.emit({ component: this.component, role: this.role })
  }

  updateRoleHours(rateCardMinutes: number) {
    const role = plainToInstance(Role, { ...this.role, rateCardMinutes })
    this.onUpdateRole.emit({ component: this.component, role })
  }

  formatDisplayValue = (val) => formatHours(val / 60)
  parseDisplayValue = (val) => formatDisplayHoursToMinutes(val)

  getFieldToDisplay = (fieldName: string) => getCrFieldToDisplay<RoleWithChanges>(fieldName, this.role)
  showDiff = (fieldName: string): boolean => showCrDifferences<RoleWithChanges>(this.config, this.role, fieldName)

  formatHours = (value: number): string => this.formatHoursPipe.transform(value)
  formatPrice = (value: number | Money): string => {
    const amount = value instanceof Money ? value.amount : value
    return isNil(value) ? DEFAULT_PLACEHOLDER : this.convertCurrencyPipe.transform(amount, this.config?.currency)
  }

  showNetDelta = (): boolean =>
    !!(
      this.config?.isChangeRequest &&
      this.config?.showNetDelta &&
      ((this.role.oldRateCardHours != null && this.role.getRateCardHours() !== this.role.oldRateCardHours) ||
        ((this.role.isAdded || !this.role.id) && (this.role.getRateCardHours() ?? 0) > 0))
    )

  getHoursDelta(): number {
    return getNumberDeltaValue(this.role.getRateCardHours(), this.role.oldRateCardHours)
  }

  showPriceDelta = (): boolean =>
    this.showNetDelta() &&
    (this.role['oldRateCardRate'] != null || this.role['oldRateCardCost'] != null || this.role.isAdded || !this.role.id)

  getPriceDelta(): number {
    const currentPrice = this.role.getUserSellingPrice({ markup: this.component.costPlusValue })?.amount
    const oldPrice =
      getRoleOldSellingRate(this.role, this.component.costPlusValue ?? 0) * (this.role.oldRateCardHours ?? 0)
    return getNumberDeltaValue(currentPrice, oldPrice)
  }

  @HostBinding('class.change-request-removed') get isChangeRequestRemoved() {
    return this.config?.isChangeRequest && (this.config?.showDiff || this.config?.showNetDelta) && this.role.isRemoved
  }
  @HostBinding('class.change-request-added') get isChangeRequestAdded() {
    return (
      this.config?.isChangeRequest &&
      (this.config?.showDiff || this.config?.showNetDelta) &&
      (this.role.isAdded || !this.role.id)
    )
  }

  protected readonly TableColumnKey = TableColumnKey
  protected readonly Privilege = Privilege
  protected readonly DEFAULT_PLACEHOLDER = DEFAULT_PLACEHOLDER
}
